import useDateLocale from '@/common/hooks/useDateLocale'
import useUserMetadata from '@/user/hooks/useUserMetadata'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  CloseButton,
  Link,
} from '@chakra-ui/react'
import { useCurrentAnnouncementSubscription } from '@gql'
import { AnnouncementType } from '@rolebase/shared/model/release'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  announcementToShow,
  formatAnnouncementDates,
  isValidAnnouncementUrl,
} from '../announcement'

const statusByType: Record<AnnouncementType, 'info' | 'warning'> = {
  upcoming: 'info',
  maintenance: 'warning',
}

// #77 — an update to come or a maintenance, written by Yohan in the console.
// Shown everywhere once signed in, in every organization; closing it hides it
// for this person on all their devices (user metadata).
export default function AnnouncementBanner() {
  const { t } = useTranslation()
  const dateLocale = useDateLocale()
  const { metadata, setMetadata } = useUserMetadata()

  // Fixed when the subscription opens: an announcement created later ends
  // later, so it still matches. The end of the current one is handled below.
  const since = useMemo(() => new Date().toISOString(), [])
  const { data } = useCurrentAnnouncementSubscription({
    variables: { now: since },
  })

  // Re-render at the planned end, so the banner leaves without a reload
  const [now, setNow] = useState(() => new Date())
  const announcement = announcementToShow(
    data?.announcement[0],
    metadata?.dismissedAnnouncementId,
    now
  )
  useEffect(() => {
    if (!announcement) return
    const delay = new Date(announcement.endsAt).getTime() - Date.now()
    // setTimeout overflows past ~24.8 days: check again then
    const timeout = setTimeout(
      () => setNow(new Date()),
      Math.min(Math.max(delay, 0), 2 ** 31 - 1)
    )
    return () => clearTimeout(timeout)
  }, [announcement?.id, announcement?.endsAt])

  if (!announcement) return null
  const type = announcement.type as AnnouncementType

  return (
    <Alert
      status={statusByType[type] ?? 'info'}
      alignItems="flex-start"
      py={2}
      role="status"
    >
      <AlertIcon />
      <Box flex={1} minW={0}>
        <AlertTitle>
          {t(`AnnouncementBanner.types.${type}`)} ·{' '}
          {formatAnnouncementDates(
            announcement.startsAt,
            announcement.endsAt,
            dateLocale
          )}
        </AlertTitle>
        <AlertDescription>
          {announcement.message}
          {announcement.url && isValidAnnouncementUrl(announcement.url) && (
            <>
              {' '}
              <Link href={announcement.url} isExternal fontWeight="semibold">
                {t('AnnouncementBanner.more')} ↗
              </Link>
            </>
          )}
        </AlertDescription>
      </Box>
      <CloseButton
        size="sm"
        aria-label={t('common.close')}
        onClick={() => setMetadata('dismissedAnnouncementId', announcement.id)}
      />
    </Alert>
  )
}
